import classNames from 'classnames';
import React, { forwardRef } from 'react';
import { Icon } from '@material-ui/core';
import Tooltip from '../../atoms/Tooltip';

export type FormProps = React.HTMLAttributes<HTMLFormElement>;

const Form: React.FC<FormProps> & {
  Fileld: typeof FormFileld;
  FileldChild: typeof FileldChild;
  Actions: typeof FormActions;
} = ({ children, ...props }) => <form {...props}>{children}</form>;

export interface FormFileldProps extends React.HTMLAttributes<HTMLElement> {
  className?: string;

  /**
   * ラベル
   */
  label?: string | React.ReactNode;

  /**
   * ラベルの横幅
   */
  labelWidth?: '3s' | '2s' | 's' | 'm' | 'ml' | 'l' | '2l' | 'full';

  /**
   * inputのid
   */
  labelFor?: string;

  /**
   * ラベルのクラス
   */
  labelClassName?: string;

  /**
   * 説明文
   */
  explain?: string | React.ReactNode;

  /**
   * ヘルプテキスト
   */
  help?: string | React.ReactNode;

  /**
   * メッセージ
   * error, valid propsがtrueの時に表示される.
   */
  message?: string | React.ReactNode;

  /**
   * 必須項目かどうか
   * ラベルの見た目部分のみのため、
   */
  isRequired?: boolean;

  /**
   * 必須テキスト. デフォルトは `*`
   */
  requiredText?: string;

  /**
   * ラベルを水平に設置する
   */
  isHorizontal?: boolean;

  /**
   * エラーかどうか
   */
  error?: boolean;

  /**
   * ワーニング対象かどうか
   */
  warning?: boolean;

  /**
   * 有効かどうか
   */
  valid?: boolean;

  /**
   * ツールチップ
   */
  tooltip?: string | React.ReactNode;

  /**
   * Form.Fileld をネストして使用する際に trueにする (Form.FileldChildで代替可能)
   */
  isChild?: boolean;
}

const FormFileld = forwardRef<HTMLDivElement, FormFileldProps>(
  (
    {
      className,
      label,
      labelWidth,
      labelFor,
      labelClassName,
      explain,
      help,
      message,
      isRequired = false,
      requiredText = '*',
      isHorizontal = false,
      error = false,
      warning = false,
      valid = false,
      tooltip,
      isChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const baseClass = isChild ? 'uc-form-field__child' : 'uc-form-field';
    const classes = classNames(baseClass, isHorizontal && 'uc-form-field--horizontal', className);

    // メッセージエリアの表示
    const renderMessageArea = () => {
      // 1. エラー & メッセージありの場合、エラーメッセージを表示する
      if (error && message) {
        return (
          <div className='uc-form-field__message uc-form-field__message--error' data-testid='Form-messageError'>
            <Icon>error</Icon>
            <span className='u-text-middle'>{message}</span>
          </div>
        );
      }
      // 2. 有効 & メッセージありの場合、有効メッセージを表示する
      if (valid && message) {
        return (
          <div className='uc-form-field__message uc-form-field__message--success' data-testid='Form-messageValid'>
            <Icon>check_circle</Icon>
            {message}
          </div>
        );
      }
      // 3. ワーニング対象 & メッセージありの場合、ワーニングメッセージを表示する
      if (warning && message) {
        return (
          <div className='uc-form-field__message uc-form-field__message--error' data-testid='Form-messageWarning'>
            <Icon className='u-text-warning'>warning</Icon>
            <span className='u-text-middle'>{message}</span>
          </div>
        );
      }

      return undefined;
    };

    return (
      <div className={classes} ref={ref} {...props}>
        {label && (
          <label
            htmlFor={labelFor}
            data-testid='Form-label'
            className={classNames('uc-form-field__label', labelWidth && `uc-form-field__label--w${labelWidth}`, labelClassName)}>
            {label}
            {isRequired && (
              <span className='u-text-danger' data-testid='Form-isRequired'>
                {requiredText}
              </span>
            )}
            {tooltip && (
              <Tooltip title={tooltip} arrow placement='top-start' data-testid='Form-tooltip'>
                <Icon className='u-text-secondary u-icon-s u-text-middle u-ml4'>help</Icon>
              </Tooltip>
            )}
          </label>
        )}
        <div className='uc-form-field__control'>
          {explain && (
            <div className='uc-form-field__explain' data-testid='Form-explain'>
              {explain}
            </div>
          )}
          {children}
          {help && (
            <div className='uc-form-field__help' data-testid='Form-help'>
              {help}
            </div>
          )}
          {renderMessageArea()}
        </div>
      </div>
    );
  }
);

export type FormFileldChildProps = FormFileldProps;

const FileldChild: React.FC<FormFileldChildProps> = ({ children, ...props }) => {
  return (
    <FormFileld {...props} isChild>
      {children}
    </FormFileld>
  );
};

export type FormActionsProps = React.HTMLAttributes<HTMLElement>;

const FormActions: React.FC<FormActionsProps> = ({ className, children, ...props }) => {
  const classes = classNames('uc-form-action', className);
  return (
    <div className={classes} {...props}>
      {children}
    </div>
  );
};

Form.Fileld = FormFileld;
Form.FileldChild = FileldChild;
Form.Actions = FormActions;

export default Form;
